import React, { useCallback, useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import PropTypes from "prop-types";
import {
  Modal,
  Input,
  Button,
  Table,
  Tag,
  Alert,
  Tooltip,
  Typography,
  Space,
  Select,
  List,
  Card,
} from "antd";
import {
  PlusOutlined,
  CheckOutlined,
  MailOutlined,
  UserOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { SearchBar } from "@authzsvc/common-react-components";
import {
  searchForPerson,
  searchForGroup,
  updateGroupIdentityMembership,
} from "../../actions/api";
import {
  showErrorNotification,
  showSuccessNotification,
  showWarningNotification,
  validateEmail,
} from "../../util";
import { errorAction, types } from "@authzsvc/api-lib";
import {
  getMemberTypeLabel,
  identityMaxSearchLimit,
  renderMemberIdentifier,
  searchMaxLimit,
} from "../../constants";

/**
 * AddMemberSearchModal
 *
 * A modal component for searching and adding group members by identity or group.
 * Supports searching for people and groups, adding by email with intelligent matching,
 * and optional comments. When adding by email, it first checks if any search results
 * have matching email addresses and adds those users by ID, otherwise creates a new user.
 *
 * Also supports bulk operations when selectedGroups is provided, allowing operations
 * across multiple groups simultaneously. Can be used for both addition and removal operations.
 * The bulk mode uses a simplified approach that attempts to add members to all selected
 * groups without pre-checking membership status for optimal performance.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.title] - Modal title (currently defaults to "Add Members").
 * @param {function} props.onSelect - Callback when a member is selected. Receives (record, comment, emailUsedInSearch, selectedGroups).
 * @param {string} [props.searchValue] - Initial search value for the input.
 * @param {function} props.onCreateByEmail - Callback when adding by email. Receives (email).
 * @param {boolean} props.isReplicaGroup - If true, disables add actions.
 * @param {boolean} [props.isModalOpen] - Controls modal visibility.
 * @param {function} [props.onModalClose] - Callback when modal closes.
 * @param {Array} [props.members] - Array of current group members, used to display membership status and prevent re-adding existing members.
 * @param {Array<Object>} [props.nonAddableMembers] - Array of objects describing members that cannot be added.
 *   Each object should have:
 *     - {string} [type] ("Identity" or "Group") - Optional. If provided, matches by type and identifier. If not provided, matches by identifier only.
 *     - {string} identifier (id of the identity or group)
 *     - {string} message (reason why it cannot be added)
 * @param {boolean} [props.hideComment] - If true, hides comment input for each row.
 * @param {string} [props.addButtonText] - Text for the add button (defaults to "Add Members").
 * @param {string} [props.groupId] - Group ID for updating mailing addresses.
 * @param {function} [props.onMailingAddressChange] - Callback when mailing address is changed successfully.
 * @param {boolean} [props.hideChangeMailingAddress] - If true, hides the "Change mailing address" button for existing members.
 * @param {Array} [props.selectedGroups] - Array of selected groups for bulk operation. When provided, enables bulk mode.
 * @param {boolean} [props.isRemovalMode] - If true, shows removal-specific UI and behavior.
 * @param {boolean} [props.hideButton] - If true, hides the trigger button (for modal-only usage).
 *
 * @returns {JSX.Element}
 */
const AddMemberSearchModal = ({
  title = "Add Members",
  onSelect,
  searchValue,
  onCreateByEmail,
  isReplicaGroup = false,
  isModalOpen,
  onModalClose,
  members,
  nonAddableMembers = [],
  hideComment = false,
  addButtonText = "Add Members",
  groupId,
  onMailingAddressChange,
  hideChangeMailingAddress = false,
  selectedGroups = [],
  isRemovalMode = false,
  hideButton = false,
}) => {
  const dispatch = useDispatch();

  // Determine if we're in bulk mode
  const isBulkMode = selectedGroups && selectedGroups.length > 0;

  // Helper function to handle action button click
  const handleActionClick = (record) => {
    const comment = commentInputs[record.id] || "";
    handleSelectWithComment(record, comment);

    setCommentInputs((prev) => {
      const newInputs = { ...prev };
      delete newInputs[record.id];
      return newInputs;
    });
  };

  // Helper function to render bulk mode actions
  const renderBulkModeAction = (membershipStatus, record, nonAddable) => {
    // Filter out self-membership groups (group trying to add itself)
    const validGroups = selectedGroups.filter((group) => {
      const isSelfMembership = record.groupIdentifier && record.id === group.id;
      return !isSelfMembership;
    });

    const TRUNCATE_THRESHOLD = 5; // Show up to this many groups before truncating
    const remainingCount = validGroups.length - TRUNCATE_THRESHOLD;
    const shouldTruncate = remainingCount > 1; // Only truncate if more than 1 remaining

    const displayGroups = shouldTruncate
      ? validGroups.slice(0, TRUNCATE_THRESHOLD)
      : validGroups;

    const tooltipContent = (
      <div>
        <div style={{ marginBottom: 4, textDecoration: "underline" }}>
          Will attempt to {isRemovalMode ? "remove from" : "add to"}:
        </div>
        <div style={{ marginLeft: 8 }}>
          <ul style={{ margin: 0, paddingLeft: 16 }}>
            {displayGroups.map((group, index) => (
              <li key={index}>{group.groupIdentifier}</li>
            ))}
          </ul>
          {shouldTruncate && (
            <div
              style={{ fontStyle: "italic", marginTop: 4, fontSize: "12px" }}
            >
              ...and {remainingCount} more
            </div>
          )}
        </div>
      </div>
    );

    const actionButton = (
      <Button
        color={isRemovalMode ? "red" : "green"}
        variant="solid"
        loading={isLoading}
        disabled={!!nonAddable}
        onClick={() => handleActionClick(record)}
      >
        {addButtonText}
      </Button>
    );

    const showCommentInput = !hideComment;

    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <Space.Compact block>
          {showCommentInput && (
            <Input
              placeholder="Optional comment"
              value={commentInputs[record.id] || ""}
              onChange={(e) => {
                setCommentInputs((prev) => ({
                  ...prev,
                  [record.id]: e.target.value,
                }));
              }}
              disabled={isLoading || !!nonAddable}
              onPressEnter={() => handleActionClick(record)}
            />
          )}
          {nonAddable ? (
            <Tooltip title={nonAddable.message}>{actionButton}</Tooltip>
          ) : (
            <Tooltip title={tooltipContent} placement="left">
              {actionButton}
            </Tooltip>
          )}
        </Space.Compact>
      </div>
    );
  };

  // Helper function to render existing member actions
  const renderExistingMemberAction = (
    record,
    isIdentity,
    isValidEmailSearch
  ) => {
    // If it's an existing identity member and search term is a valid email, show change mailing address button
    if (
      isIdentity &&
      isValidEmailSearch &&
      groupId &&
      !hideChangeMailingAddress
    ) {
      // Find the current member to check their current email
      const currentMember = members.find(
        (member) => member.memberIdentity?.id === record.id
      );

      // Get current email: prioritize emailAddress over memberIdentity.email (same logic as MailingAddressSelector)
      const currentEmail =
        currentMember?.emailAddress ||
        currentMember?.memberIdentity?.email ||
        "";

      // Check if searched email matches current email
      const isEmailAlreadySet = currentEmail === searchedEmail;

      const changeButton = (
        <Button
          size="small"
          color="primary"
          variant="filled"
          icon={<MailOutlined />}
          loading={isLoading}
          onClick={() => handleChangeMailingAddress(record.id)}
          disabled={isLoading || isEmailAlreadySet}
        >
          Change mailing address
        </Button>
      );

      return (
        <Space size="small" style={{ width: "100%" }}>
          <Tag icon={<CheckOutlined />} color="success">
            Member
          </Tag>
          {isEmailAlreadySet ? (
            <Tooltip title="This email is already set as the mailing address for this member">
              {changeButton}
            </Tooltip>
          ) : (
            changeButton
          )}
        </Space>
      );
    }

    return (
      <Tag icon={<CheckOutlined />} color="success">
        {isBulkMode ? "Already Member" : "Member"}
      </Tag>
    );
  };

  // Helper function to render standard add/remove actions
  const renderStandardAction = (record, nonAddable) => {
    const actionButtonText = isRemovalMode
      ? "Remove"
      : isBulkMode
      ? addButtonText
      : "Add";

    const actionButton = (
      <Button
        color={isRemovalMode ? "red" : "green"}
        variant="solid"
        loading={isLoading}
        disabled={!!nonAddable || (!isRemovalMode && isCurrentMember(record))}
        onClick={() => handleActionClick(record)}
      >
        {actionButtonText}
      </Button>
    );

    const showCommentInput = !hideComment && !(isBulkMode && isRemovalMode);

    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <Space.Compact block>
          {showCommentInput && (
            <Input
              placeholder="Optional comment"
              value={commentInputs[record.id] || ""}
              onChange={(e) => {
                setCommentInputs((prev) => ({
                  ...prev,
                  [record.id]: e.target.value,
                }));
              }}
              disabled={isLoading || !!nonAddable}
              onPressEnter={() => handleActionClick(record)}
            />
          )}
          {nonAddable ? (
            <Tooltip title={nonAddable.message}>{actionButton}</Tooltip>
          ) : (
            actionButton
          )}
        </Space.Compact>
      </div>
    );
  };

  const identityTypeOptions = [
    { key: "All", label: "All" },
    { key: "Group", label: "Group" },
    { key: "Person", label: "Person" },
    { key: "Application", label: "Application" },
    { key: "Service", label: "Service" },
    { key: "Secondary", label: "Secondary" },
  ];

  const [visible, setVisible] = useState(false);
  const [searchIsLoading, setSearchIsLoading] = useState(false);
  const [emailAddress, setEmailAddress] = useState("");
  const [internalSearchValue, setInternalSearchValue] = useState("");
  const [dataSource, setDataSource] = useState([]);
  const [commentInputs, setCommentInputs] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIdentityType, setSelectedIdentityType] = useState("All");
  const [currentFilter, setCurrentFilter] = useState("Contains");
  const [searchedEmail, setSearchedEmail] = useState("");
  const [limitReached, setLimitReached] = useState(false);

  useEffect(() => {
    setVisible(isModalOpen);
  }, [isModalOpen]);

  const handleChangeMailingAddress = async (identityId) => {
    if (!groupId || !validateEmail(searchedEmail)) return;

    setIsLoading(true);

    // CERN emails set to null will default to memberIdentity.email (same logic as MailingAddressSelector)
    const emailToUpdate = searchedEmail?.endsWith("@cern.ch")
      ? null
      : searchedEmail;

    const response = await dispatch(
      updateGroupIdentityMembership(
        groupId,
        identityId,
        undefined,
        emailToUpdate
      )
    );

    if (response.type === errorAction(types.UPDATE_GROUP_IDENTITY_MEMBERSHIP)) {
      showErrorNotification(
        "Failed to update email address",
        response.payload?.data?.message || "Please try again later"
      );
    } else {
      showSuccessNotification("", "Email address updated successfully");

      if (onMailingAddressChange) {
        onMailingAddressChange();
      }
    }

    setIsLoading(false);
  };

  const getNonAddable = (record) => {
    if (!nonAddableMembers || !record) return null;

    return nonAddableMembers.find((item) => {
      if (!item.type) {
        return item.identifier === record.id;
      }
      if (record.displayName && record.upn) {
        return item.type === "Identity" && item.identifier === record.id;
      } else if (record.groupIdentifier) {
        return item.type === "Group" && item.identifier === record.id;
      }
      return false;
    });
  };

  const modalColumns = [
    {
      title: "Identifier",
      dataIndex: "displayName",
      render: (text, record) => {
        const member = record.groupIdentifier
          ? { memberGroup: record }
          : { memberIdentity: record };
        return renderMemberIdentifier(member);
      },
    },
    {
      title: "Type",
      dataIndex: "type",
      render: (text, record) => {
        const memberRecord = record.groupIdentifier
          ? { memberGroup: record }
          : { memberIdentity: record };

        return getMemberTypeLabel(memberRecord);
      },
    },
    {
      title: "Action",
      dataIndex: "id",
      render: (_, record) => {
        const isMember = isCurrentMember(record);
        const nonAddable = getNonAddable(record);
        const isIdentity = record.displayName && record.upn; // Check if it's an identity (not a group)
        const isValidEmailSearch = validateEmail(searchedEmail);

        if (isBulkMode) {
          return renderBulkModeAction(null, record, nonAddable);
        }

        if (isMember) {
          return renderExistingMemberAction(
            record,
            isIdentity,
            isValidEmailSearch
          );
        }

        return renderStandardAction(record, nonAddable);
      },
    },
  ];

  const isCurrentMember = (record) => {
    if (!members || !record) return false;
    return members.some((member) => {
      if (record.id && member) {
        if (member.memberIdentity?.id) {
          return record.id === member.memberIdentity?.id;
        }
        if (member.memberGroup?.id) {
          return record.id === member.memberGroup?.id;
        }
        if (member.key) {
          return record.id === member.key;
        }
      }
      return false;
    });
  };

  const handleIdentityTypeMenuClick = ({ key }) => {
    setSelectedIdentityType(key);

    if (internalSearchValue?.length >= 2) {
      handleSearch(internalSearchValue, currentFilter, undefined, key);
    }
  };

  const clearInputs = () => {
    setEmailAddress("");
    setInternalSearchValue("");
    setDataSource([]);
    setSearchedEmail("");
  };

  const identityTypeSelector = (
    <Select
      value={selectedIdentityType}
      onChange={(value) => handleIdentityTypeMenuClick({ key: value })}
      options={identityTypeOptions.map((opt) => ({
        value: opt.key,
        label: opt.label,
      }))}
      style={{
        borderRight: "1px solid #d9d9d9",
        marginRight: 0,
      }}
      popupMatchSelectWidth={false}
      suffixIcon={<UserOutlined />}
    />
  );

  const handleSelectWithComment = async (record, comment = "") => {
    setIsLoading(true);
    const emailUsedInSearch = validateEmail(searchedEmail)
      ? searchedEmail
      : null;

    if (isBulkMode) {
      await onSelect(record, comment, emailUsedInSearch, selectedGroups);
    } else {
      await onSelect(record, comment, emailUsedInSearch);
    }
    setIsLoading(false);
  };

  const openUpdateModal = (e) => {
    e.preventDefault();
    setVisible(true);
    clearInputs();
  };

  const handleCancel = () => {
    setVisible(false);
    if (onModalClose) {
      onModalClose();
    }
    clearInputs();
  };

  const handleSearch = useCallback(
    async (text, filter, limit, identityType = selectedIdentityType) => {
      const trimmedText = text?.trim() || "";

      setCurrentFilter(filter);
      setEmailAddress(trimmedText);
      setSearchedEmail(trimmedText);
      setSearchIsLoading(true);

      const promises = [];

      if (identityType === "All" || identityType === "Group") {
        const groupsPromise = dispatch(
          searchForGroup({
            filter: `groupIdentifier:${filter}:${trimmedText}`,
            sort: "groupIdentifier",
          })
        );
        promises.push(
          groupsPromise.then((result) => ({
            type: "groups",
            data: result.payload.data,
          }))
        );
      }

      if (identityType !== "Group") {
        const searchIdentityType = identityType === "All" ? null : identityType;
        const identitiesPromise = dispatch(
          searchForPerson(trimmedText, searchIdentityType, null, filter)
        );
        promises.push(
          identitiesPromise.then((result) => ({
            type: "identities",
            data: result.payload,
          }))
        );
      }

      setLimitReached(false);
      try {
        const results = await Promise.allSettled(promises);
        const combinedData = [];
        const failedSearches = [];
        let hasSuccessfulResults = false;

        results.forEach((result, index) => {
          if (result.status === "fulfilled") {
            const resultData = result.value;

            const isErrorResponse =
              resultData.data &&
              (resultData.data.status || resultData.data.message) &&
              !Array.isArray(resultData.data);

            if (isErrorResponse) {
              if (resultData.type === "groups") {
                failedSearches.push("groups");
              } else if (resultData.type === "identities") {
                failedSearches.push("identities");
              }
            } else if (resultData.type === "groups") {
              if (resultData.data && Array.isArray(resultData.data)) {
                hasSuccessfulResults = true;
                if (resultData.data.length === searchMaxLimit) {
                  setLimitReached(true);
                }
                combinedData.push(...resultData.data);
              }
            } else if (resultData.type === "identities") {
              if (resultData.data && Array.isArray(resultData.data)) {
                hasSuccessfulResults = true;
                if (resultData.data.length === identityMaxSearchLimit) {
                  setLimitReached(true);
                }
                combinedData.push(...resultData.data);
              }
            }
          } else {
            if (identityType === "All") {
              failedSearches.push(index === 0 ? "groups" : "identities");
            } else if (identityType === "Group") {
              failedSearches.push("groups");
            } else {
              failedSearches.push("identities");
            }
          }
        });
        try {
          const results = await Promise.allSettled(promises);
          const combinedData = [];
          const failedSearches = [];
          let hasSuccessfulResults = false;

          results.forEach((result, index) => {
            if (result.status === "fulfilled") {
              const resultData = result.value;

              const isErrorResponse =
                resultData.data &&
                (resultData.data.status || resultData.data.message) &&
                !Array.isArray(resultData.data);

              if (isErrorResponse) {
                if (resultData.type === "groups") {
                  failedSearches.push("groups");
                } else if (resultData.type === "identities") {
                  failedSearches.push("identities");
                }
              } else if (resultData.type === "groups") {
                if (resultData.data && Array.isArray(resultData.data)) {
                  hasSuccessfulResults = true;
                  if (resultData.data.length === searchMaxLimit) {
                    setLimitReached(true);
                  }
                  combinedData.push(...resultData.data);
                }
              } else if (resultData.type === "identities") {
                if (resultData.data && Array.isArray(resultData.data)) {
                  hasSuccessfulResults = true;
                  if (resultData.data.length === identityMaxSearchLimit) {
                    setLimitReached(true);
                  }
                  combinedData.push(...resultData.data);
                }
              }
            } else {
              if (identityType === "All") {
                failedSearches.push(index === 0 ? "groups" : "identities");
              } else if (identityType === "Group") {
                failedSearches.push("groups");
              } else {
                failedSearches.push("identities");
              }
            }
          });

          setDataSource(combinedData);
          setSearchIsLoading(false);

          // Always notify user if any searches failed, regardless of whether we have results
          if (failedSearches.length > 0) {
            const failedTypes = [...new Set(failedSearches)].join(" and ");

            showWarningNotification(
              "Incomplete Search Results",
              hasSuccessfulResults
                ? `Unable to retrieve ${failedTypes}. Some data may not be displayed.`
                : `Unable to retrieve ${failedTypes}. No results to display.`
            );
          }
        } catch (error) {
          showErrorNotification(
            "Search Error",
            "An unexpected error occurred. Please try again."
          );

          setDataSource([]);
          setSearchIsLoading(false);
        }
        setDataSource(combinedData);
        setSearchIsLoading(false);

        // Always notify user if any searches failed, regardless of whether we have results
        if (failedSearches.length > 0) {
          const failedTypes = [...new Set(failedSearches)].join(" and ");

          showWarningNotification(
            "Incomplete Search Results",
            hasSuccessfulResults
              ? `Unable to retrieve ${failedTypes}. Some data may not be displayed.`
              : `Unable to retrieve ${failedTypes}. No results to display.`
          );
        }
      } catch (error) {
        showErrorNotification(
          "Search Error",
          "An unexpected error occurred. Please try again."
        );

        setDataSource([]);
        setSearchIsLoading(false);
      }
    },
    [dispatch, selectedIdentityType]
  );

  const handleSearchValueChange = useCallback((value) => {
    setInternalSearchValue(value);
  }, []);

  useEffect(() => {
    if (searchValue && visible) {
      setInternalSearchValue(searchValue);
      handleSearch(searchValue, "Contains");
    }
  }, [searchValue, visible, handleSearch]);

  const onSubmitEmailText = async (e) => {
    setSearchIsLoading(true);

    await onCreateByEmail(emailAddress);

    setSearchIsLoading(false);
    setVisible(false);
    if (onModalClose) {
      onModalClose();
    }
    clearInputs();
  };

  const renderCreatePlaceholder = () => {
    if (!validateEmail(emailAddress)) {
      return (
        <Alert
          type="info"
          showIcon
          icon={<MailOutlined style={{ marginRight: 16 }} />}
          message="If you want to add a member by email, write the user's email in the search below."
          style={{ marginBottom: 16 }}
        />
      );
    }

    const emailButtonText = isBulkMode
      ? "Add by email to all groups"
      : "Add by email";

    const emailDescriptionText = isBulkMode
      ? "If there are any users in the system that match this email, they will all be added to all selected groups. If there are none, a new user will be created and added to all your selected groups."
      : "If there are any users in the system that match this email, they will all be added. If there are none, a new user will be created and added to your group.";

    return (
      <Alert
        type="info"
        showIcon
        icon={<MailOutlined style={{ marginRight: 16 }} />}
        message={
          <>
            <Typography.Title level={5} style={{ margin: 0 }}>
              Trying to add by email?
            </Typography.Title>
            <Typography.Text style={{ display: "block", marginBottom: 8 }}>
              {emailDescriptionText}
            </Typography.Text>
            <Button
              id="submitButton"
              color="green"
              variant="solid"
              onClick={onSubmitEmailText}
              disabled={!validateEmail(emailAddress) || searchIsLoading}
              style={{ margin: "8px 0" }}
            >
              {emailButtonText}
            </Button>
          </>
        }
        style={{ marginBottom: 16 }}
      />
    );
  };

  const renderSelectedGroups = () => {
    if (!isBulkMode) {
      return null;
    }

    return (
      <>
        <Card
          title={
            <Space>
              <TeamOutlined />
              <span>Selected Groups ({selectedGroups.length})</span>
            </Space>
          }
          size="small"
          style={{ marginBottom: 16 }}
        >
          <List
            size="small"
            dataSource={selectedGroups}
            renderItem={(group) => (
              <List.Item>
                <Typography.Text strong>
                  {group.groupIdentifier}
                </Typography.Text>
                {group.description && (
                  <Typography.Text type="secondary" style={{ marginLeft: 8 }}>
                    {group.description}
                  </Typography.Text>
                )}
              </List.Item>
            )}
            style={{ maxHeight: 100, overflow: "auto" }}
          />
        </Card>
        <Alert
          message={`Will attempt to ${
            isRemovalMode ? "unsubscribe from" : "add to"
          } all ${
            selectedGroups.length
          } groups. You'll get notifications showing successes, ${
            isRemovalMode ? "non-memberships" : "existing memberships"
          }, and failures.`}
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
      </>
    );
  };

  return (
    <>
      {!hideButton && (
        <Button
          id="addButton"
          icon={<PlusOutlined />}
          onClick={openUpdateModal}
          disabled={isReplicaGroup}
          color="green"
          variant="solid"
        >
          {addButtonText}
        </Button>
      )}
      <Modal
        title={title}
        footer={null}
        open={visible}
        centered
        onCancel={handleCancel}
        width="90%"
      >
        {!isRemovalMode && renderCreatePlaceholder()}

        <p style={{ marginTop: "0.6rem" }}>Type at least 2 characters of:</p>
        <ul>
          <li>a user's email address, login or name (first or last name)</li>
          <li>a group's name or identifier</li>
        </ul>
        {renderSelectedGroups()}
        <div style={{ marginBottom: 16 }}>
          <SearchBar
            placeholder="Search for users or groups..."
            onSearch={handleSearch}
            searchValue={internalSearchValue}
            onSearchValueChange={handleSearchValueChange}
            filter={true}
            filterPlacement="before"
            searchOnClear={false}
            allowClear={true}
            addonBefore={identityTypeSelector}
          />
        </div>

        {limitReached && (
          <Alert
            message="There might be more results that are not displayed. Please refine your search criteria."
            type="warning"
            showIcon
          />
        )}

        <Table
          style={{ marginTop: "10px" }}
          loading={searchIsLoading}
          rowKey="id"
          columns={modalColumns}
          dataSource={dataSource}
        />
      </Modal>
    </>
  );
};

AddMemberSearchModal.propTypes = {
  title: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  searchValue: PropTypes.string,
  onCreateByEmail: PropTypes.func,
  isReplicaGroup: PropTypes.bool,
  isModalOpen: PropTypes.bool,
  onModalClose: PropTypes.func,
  members: PropTypes.arrayOf(
    PropTypes.shape({
      memberIdentity: PropTypes.shape({
        id: PropTypes.string,
      }),
      memberGroup: PropTypes.shape({
        id: PropTypes.string,
      }),
      key: PropTypes.string,
    })
  ),
  nonAddableMembers: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.oneOf(["Identity", "Group"]),
      identifier: PropTypes.string.isRequired,
      message: PropTypes.string.isRequired,
    })
  ),
  hideComment: PropTypes.bool,
  addButtonText: PropTypes.string,
  groupId: PropTypes.string,
  onMailingAddressChange: PropTypes.func,
  hideChangeMailingAddress: PropTypes.bool,
  selectedGroups: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      groupIdentifier: PropTypes.string.isRequired,
      description: PropTypes.string,
    })
  ),
  isRemovalMode: PropTypes.bool,
  hideButton: PropTypes.bool,
};

export default AddMemberSearchModal;
